import { useMutation, useQueryClient } from '@tanstack/react-query';

import { enrollmentService } from '@/services/enrollment-service';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { enrollmentMutationKeys, enrollmentQueryKeys } from './query-keys';

/**
 * Revoke an enrollment credential: future enrollments stop immediately;
 * agents it already enrolled keep their own identities and are unaffected.
 */
export function useRevokeEnrollmentCredential(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation<void, Error, UUID>({
    mutationKey: enrollmentMutationKeys.revokeCredential(tenantId),
    mutationFn: async (credentialId) => {
      if (!tenantId) throw new Error('Missing tenant id');

      await enrollmentService.revokeCredential(tenantId, credentialId);

      await pollForResource(async () => {
        const credentials = await enrollmentService.listCredentials(tenantId);
        const revoked = credentials.find(
          (c) => c.credentialId === credentialId && c.isRevoked
        );
        return revoked ?? null;
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: enrollmentQueryKeys.credentialsByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
